/**
 * Frontend-specific attribute rules configuration
 *
 * These rules define how attributes interact with each other in the UI.
 * Backend provides attribute definitions (type, required, options, etc.),
 * while this file defines frontend behavior like visibility and dependencies.
 */

import { ATTR_EXPERIENCED_NAME, ATTR_PRESENT_NAME } from '../constants';

export interface AttributeVisibilityRule {
  /**
   * The attribute name this rule applies to
   */
  attributeName: string;

  /**
   * Function that determines if this attribute should be visible
   * @param getAttributeValue - Function to get the value of any attribute by name
   * @returns true if the attribute should be visible, false otherwise
   */
  isVisible: (
    getAttributeValue: (name: string) => boolean | string | number | undefined
  ) => boolean;
}

/**
 * Visibility rules for attributes
 * Defines which attributes are visible based on other attribute values
 */
export const MODIFIER_VISIBILITY_RULES: AttributeVisibilityRule[] = [
  {
    attributeName: ATTR_PRESENT_NAME,
    isVisible: () => true, // Always visible
  },
  {
    attributeName: ATTR_EXPERIENCED_NAME,
    isVisible: (getAttributeValue) => {
      // Visible only if "present" is true
      return getAttributeValue(ATTR_PRESENT_NAME) === true;
    },
  },
  // All other attributes are visible if "experienced" is true
  // This is handled as a default case in the component
];

/**
 * Default visibility behavior for attributes not explicitly defined in rules
 * @param attributeName - The name of the attribute
 * @param getAttributeValue - Function to get attribute values
 * @returns true if the attribute should be visible by default
 */
export const getDefaultVisibility = (
  attributeName: string,
  getAttributeValue: (name: string) => boolean | string | number | undefined
): boolean => {
  // Check if there's an explicit rule for this attribute
  const explicitRule = MODIFIER_VISIBILITY_RULES.find(
    (rule) => rule.attributeName === attributeName
  );
  if (explicitRule) {
    return explicitRule.isVisible(getAttributeValue);
  }

  // Default: visible if "experienced" is true (for all remaining attributes)
  return getAttributeValue(ATTR_EXPERIENCED_NAME) === true;
};
